import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MapStoreService } from '@app/energy-map/services/map-store.service';
import { mapboxAttribution, mapboxLogoAttribution } from '@app/shared/constants';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { Luminaire } from '@app/shared/models/energiekarte/luminaire';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { Excel, ExcelService } from '@app/shared/services/api/excel.service';
import { StreetlightApiService } from '@app/shared/services/api/streetlight.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { saveFile } from '@app/shared/utility';
import { cssVariables } from '@env/css-vars';
import { TranslateService } from '@ngx-translate/core';
import L from 'leaflet';
import { keys, map, values } from 'lodash';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, first, map as rxjsMap } from 'rxjs/operators';

@Component({
  selector: 'app-map-side-controls',
  templateUrl: './map-side-controls.component.html',
  styleUrls: ['./map-side-controls.component.scss'],
})
export class MapSideControlsComponent implements OnInit {
  streetlightDownloadVisible = true;
  isMapLayersVisible = false;
  isSatteliteView = false;
  logo = L.control.zoom({ position: 'bottomleft' });

  constructor(
    private overlayLoaderService: OverlayLoaderService,
    private streetlightsService: StreetlightApiService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private translate: TranslateService,
    private excelService: ExcelService,
    private matDialog: MatDialog,
    private mapStoreService: MapStoreService,
    private authService: AuthService,
    private localNumberPipe: LocalNumberPipe
  ) {}

  ngOnInit() {
    this.streetlightDownloadVisible = this.authService.permissions.ENERGY_MAP_STREETLIGHTS_DOWNLOAD;
  }

  toggleLayersView = () => (this.isMapLayersVisible = !this.isMapLayersVisible);

  setLayerToStreetLayer = () => {
    if (this.mapStoreService.map) {
      this.mapStoreService.map.removeLayer(this.mapStoreService.satelliteTileLayer);
      this.mapStoreService.map.addLayer(this.mapStoreService.streetTileLayer);
      this.mapStoreService.map.attributionControl.removeAttribution(mapboxAttribution);
      this.mapStoreService.map.removeControl(this.logo);
      this.mapStoreService.map.attributionControl.addAttribution(`© basemap.de / BKG ${moment().year()}`);
    }
    this.isSatteliteView = false;
  };

  setLayerToSatelliteLayer = () => {
    if (this.mapStoreService.map) {
      this.mapStoreService.map.removeLayer(this.mapStoreService.streetTileLayer);
      this.mapStoreService.map.addLayer(this.mapStoreService.satelliteTileLayer);
      this.mapStoreService.map.attributionControl.removeAttribution(`© basemap.de / BKG ${moment().year()}`);
      this.mapStoreService.map.attributionControl.addAttribution(mapboxAttribution);
      this.logo.onAdd = function () {
        const div = L.DomUtil.create('div', 'myclass');
        div.innerHTML = mapboxLogoAttribution;
        return div;
      };
      this.logo.addTo(this.mapStoreService.map);
    }
    this.isSatteliteView = true;
  };

  formatDataToExport(lamp: Luminaire): Record<string, string | number> {
    return {
      Gemeinde: lamp.municipality,
      Gemeindeteil: lamp.district,
      Straße: lamp.street,
      Standort: lamp.locationInfo,
      Brennstellennummer: lamp.streetLightNumber,
      Leuchtenform: lamp.luminaireForm,
      'Anzahl Leuchten': lamp.numberOfLuminaires, // eslint-disable-line
      'Anzahl Leuchtmittel': lamp.numberOfLamps, // eslint-disable-line
      Leuchtmitteltyp: lamp.lampType,
      'Leistung je Leuchtmittel [W]': this.localNumberPipe.transform(lamp.powerPerLamp), // eslint-disable-line
    };
  }

  generateExcel(csvData: Array<Record<string, string | number>>) {
    const date = moment().format('YYMMDD');
    const excelData: Excel = {
      fileName: `${this.userMunicipalitiesService.currentMunicipality.name}_Brennstellenliste_${date}.xlsx`,
      sheetName: `Luminaires`,
      headers: keys(csvData[0]),
      data: map(csvData, values),
      protectedColumns: [],
      templateCode: '',
      headerBgColor: cssVariables['--ep-primary-color'],
      secretPhrase: '',
    };

    this.excelService
      .generateExcel(excelData)
      .pipe(
        catchError(() => {
          this.overlayLoaderService.isLoading = false;
          return of(null);
        }),
        first()
      )
      .subscribe((xlsx) => {
        const blob = new Blob([xlsx], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveFile(blob, excelData.fileName);
        this.overlayLoaderService.isLoading = false;
      });
  }

  downloadStreetlights() {
    this.overlayLoaderService.isLoading = true;
    const filters = { includeNoCoordinates: true, includeNoCategory: true };
    const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
    this.streetlightsService
      .getLuminairesByMunicipalityKey(municipalityKey, filters)
      .pipe(
        rxjsMap((lamps: Luminaire[]) => (lamps.length > 0 ? lamps.map((lamp: Luminaire) => this.formatDataToExport(lamp)) : [])),
        first()
      )
      .subscribe({
        next: (csvData: Array<Record<string, string | number>>) => {
          if (csvData.length <= 0) {
            this.matDialog.open(NotificationDialogComponent, {
              data: {
                descriptionText: this.translate.instant('module.energyMap.noStretlightsInMunicipalityMessage'),
                descriptionFaClass: 'fa-exclamation-triangle fa-2x',
                descriptionFaColor: cssVariables['--ep-error-color'],
              },
              position: { top: '40px' },
              panelClass: ['-md'],
            });
            this.overlayLoaderService.isLoading = false;
            return;
          }

          this.generateExcel(csvData);
        },
        error: () => {
          this.overlayLoaderService.isLoading = false;
        },
      });
  }
}
